import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, ArrowLeft, Award, CheckCircle2, Clock, ListChecks, Mail } from 'lucide-react';
import { gestorService } from '../../shared/services';

function formatarData(valor?: string | null) {
  if (!valor) return '—';
  return new Date(valor).toLocaleDateString('pt-BR');
}

export function ManagerMemberPage() {
  const { usuarioId } = useParams<{ usuarioId: string }>();

  const { data: colaborador, isLoading, isError } = useQuery({
    queryKey: ['gestor-colaborador', usuarioId],
    queryFn: () => gestorService.getColaborador(usuarioId!),
    enabled: !!usuarioId,
    retry: false,
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (isError || !colaborador) {
    return (
      <div className="p-8 max-w-3xl mx-auto text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Colaborador não encontrado</h1>
        <p className="text-gray-600 mb-4">Ele não está vinculado a você como subordinado direto.</p>
        <Link to="/gestor" className="text-primary-600">Voltar ao Meu Time</Link>
      </div>
    );
  }

  return (
    <div className="p-8">
      <div className="max-w-5xl mx-auto">
        <Link to="/gestor" className="inline-flex items-center gap-2 text-primary-600 mb-6">
          <ArrowLeft size={16} /> Voltar ao Meu Time
        </Link>

        <div className="card">
          <h1 className="text-2xl font-bold text-gray-900">{colaborador.nome}</h1>
          <p className="text-gray-600 flex items-center gap-2 mt-1">
            <Mail size={16} /> {colaborador.email}
          </p>
          <div className="flex flex-wrap gap-4 mt-3 text-sm text-gray-600">
            <span>{colaborador.areaNome}</span>
            <span>·</span>
            <span>{colaborador.cargoNome}</span>
            <span>·</span>
            <span>Último acesso: {formatarData(colaborador.ultimoAcesso)}</span>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
          {[
            { rotulo: 'Trilhas', valor: colaborador.totalTrilhas, Icon: ListChecks },
            { rotulo: 'Concluídas', valor: colaborador.trilhasConcluidas, Icon: CheckCircle2 },
            { rotulo: 'Atrasadas', valor: colaborador.trilhasAtrasadas, Icon: AlertTriangle },
            { rotulo: 'Certificados', valor: colaborador.certificados, Icon: Award },
          ].map(({ rotulo, valor, Icon }) => (
            <div key={rotulo} className="card">
              <Icon className="text-primary-600 mb-2" size={20} />
              <p className="text-2xl font-bold text-gray-900">{valor}</p>
              <p className="text-xs text-gray-600">{rotulo}</p>
            </div>
          ))}
        </div>

        <section className="card mt-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Trilhas</h2>
            <span className="text-sm text-gray-600">Progresso médio: {colaborador.percentualMedio}%</span>
          </div>

          {colaborador.trilhas.length === 0 ? (
            <p className="text-sm text-gray-500">Nenhuma trilha atribuída a este colaborador.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th className="py-2 pr-4">Trilha</th>
                    <th className="py-2 pr-4">Progresso</th>
                    <th className="py-2 pr-4">Prazo</th>
                    <th className="py-2 pr-4">Próximo curso</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {colaborador.trilhas.map((trilha) => (
                    <tr key={trilha.trilhaId} className="border-b border-gray-100">
                      <td className="py-3 pr-4">
                        <p className="font-medium text-gray-900">{trilha.trilhaNome}</p>
                        <p className="text-xs text-gray-500">{trilha.obrigatoria ? 'Obrigatória' : 'Eletiva'}</p>
                      </td>
                      <td className="py-3 pr-4 min-w-[150px]">
                        <div className="flex items-center gap-2">
                          <div className="h-2 bg-gray-200 rounded-full overflow-hidden flex-1">
                            <div
                              className={`h-full ${trilha.concluida ? 'bg-green-600' : 'bg-primary-600'}`}
                              style={{ width: `${trilha.percentual}%` }}
                            />
                          </div>
                          <span className="w-9 text-right text-xs font-medium text-gray-700">{trilha.percentual}%</span>
                        </div>
                      </td>
                      <td className={`py-3 pr-4 ${trilha.atrasada ? 'text-red-700 font-medium' : 'text-gray-600'}`}>
                        {trilha.prazoEm ? (
                          <span className="inline-flex items-center gap-1">
                            <Clock size={14} /> {formatarData(trilha.prazoEm)}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="py-3 pr-4 text-gray-700">{trilha.proximoCursoNome ?? '—'}</td>
                      <td className="py-3">
                        {trilha.concluida ? (
                          <span className="px-2 py-1 rounded bg-green-100 text-green-800 text-xs font-medium">Concluída</span>
                        ) : trilha.atrasada ? (
                          <span className="px-2 py-1 rounded bg-red-100 text-red-800 text-xs font-medium">Atrasada</span>
                        ) : (
                          <span className="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs font-medium">Em andamento</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
